// LegalPage.tsx — (B120-1) the four public pages' frame (Privacy Policy, Terms of Service, Data deletion, Contact) and the
// reader of their text (legal-text.ts): a heading, the pages' date, then the text. Pre-rendered, so the raw HTML holds it all.
import { Fragment, type ReactNode } from "react";
import { SEOHead } from "@/components/seo/SEOHead.tsx";
import { pagesDate } from "./legal-text.ts";

const T = { ink: "#0a0a0a", inkSoft: "#6b6b6b", rule: "#e9e6df", paper2: "#f3efe5", accent: "#ff5b1f" };

// a web address (trailing punctuation left out), an email address, or **bold**
const INLINE = /(\*\*[^*]+\*\*|https?:\/\/[^\s)<]+[^\s)<.,;](?![^\s)]*<)|[A-Za-z0-9._-]+@tubeforge\.io)/g;   // (audit 10-27) not a URL that runs into a <placeholder>
function inline(s: string): ReactNode[] {
  return s.split(INLINE).map((part, i) => {
    if (i % 2 === 0) return part;
    if (part.startsWith("**")) return <strong key={i} style={{ color: T.ink }}>{inline(part.slice(2, -2))}</strong>;
    if (part.startsWith("http")) return <a key={i} href={part} style={{ color: T.ink }}>{part}</a>;
    return <a key={i} href={`mailto:${part}`} style={{ color: T.ink }}>{part}</a>;
  });
}

type Block = { kind: "h2" | "p"; text: string } | { kind: "ul" | "ol"; items: string[] };
/** Pure: the text as blocks — headings, paragraphs (their lines joined), bulleted and numbered lists */
export function blocks(text: string): Block[] {
  const out: Block[] = [];
  for (const chunk of text.split(/\n\s*\n/)) {
    let para: string[] = [], list = null as { kind: "ul" | "ol"; items: string[] } | null;
    const flush = () => { if (para.length) out.push({ kind: "p", text: para.join(" ") }); para = []; };
    for (const line of chunk.split("\n")) {
      const item = /^(- |\d+\. )(.*)$/.exec(line);
      if (item) {
        flush();
        const kind = item[1] === "- " ? "ul" : "ol";
        if (list?.kind === kind) list.items.push(item[2]); else { list = { kind, items: [item[2]] }; out.push(list); }
        continue;
      }
      list = null;
      if (line.startsWith("## ")) { flush(); out.push({ kind: "h2", text: line.slice(3) }); } else para.push(line);
    }
    flush();
  }
  return out;
}

export function LegalPage({ title, path, description, text }: { title: string; path: string; description: string; text: string }) {
  return (
    <>
      <SEOHead title={title} description={description} path={path} noindex={false} />
      <section style={{ backgroundColor: T.paper2, borderBottom: `1px solid ${T.rule}`, padding: "48px 20px 32px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.accent, marginBottom: 10 }}>Legal</p>
          <h1 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 800, letterSpacing: "-0.03em", margin: "0 0 8px", color: T.ink, fontFamily: '"Bricolage Grotesque","Inter",system-ui' }}>
            {title}
          </h1>
          <p style={{ fontSize: 14, color: T.inkSoft, margin: 0 }}>Last updated: {pagesDate()}</p>
        </div>
      </section>
      <section style={{ maxWidth: 720, margin: "0 auto", padding: "48px 20px 80px", fontSize: 15, lineHeight: 1.8, color: T.inkSoft }}>
        {blocks(text).map((b, i) => (
          <Fragment key={i}>
            {b.kind === "h2" && <h2 style={{ fontSize: 20, fontWeight: 700, color: T.ink, margin: "32px 0 12px" }}>{b.text}</h2>}
            {b.kind === "p" && <p style={{ margin: "0 0 16px" }}>{inline(b.text)}</p>}
            {b.kind === "ul" && <ul style={{ margin: "0 0 16px", paddingLeft: 24 }}>{b.items.map((t, j) => <li key={j} style={{ marginBottom: 8 }}>{inline(t)}</li>)}</ul>}
            {b.kind === "ol" && <ol style={{ margin: "0 0 16px", paddingLeft: 24 }}>{b.items.map((t, j) => <li key={j} style={{ marginBottom: 8 }}>{inline(t)}</li>)}</ol>}
          </Fragment>
        ))}
      </section>
    </>
  );
}
